import { Download } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import useNotification from '../hooks/useNotification';
import { IAbsence, ITime, ITimeCsv } from '../interfaces/Types';
import { useAppSelector } from '../store/hooks';
import { ReactComponent as TimesSvg } from '../svg/airport.svg';
import AbsenceUtils from '../utils/AbsenceUtils';
import DayjsUtils from '../utils/DayjsUtils';
import HolidayUtils from '../utils/HolidayUtils';
import { exportPdf } from '../utils/PDFExport';
import TimeUtils from '../utils/TimeUtils';
import MonthPicker from './common/MonthPicker';

const ExportPage = () => {
  const notifyContext = useNotification().notifyContext;

  const { profile, user } = useAppSelector((state) => state.auth);
  const { times, evaluateTimes } = useAppSelector((state) => state.times);
  const absences = useAppSelector((state) => state.absences.absences);

  const [days, setDays] = useState<Dayjs[]>([]);
  const [filteredTimes, setFilteredTimes] = useState<ITime[] | null>(null);
  const [filteredAbsences, setFilteredAbsences] = useState<IAbsence[] | null>(null);
  const [timesExport, setTimesExport] = useState<ITimeCsv[] | null>(null);
  const [downloadBlocked, setDownloadBlocked] = useState<boolean>(true);

  const handleDateRangePicker = (dates: Dayjs[]) => setDays(dates);

  /* 
  load all times from Firestore DB, either the select month,
  or the current month, if nothing selected yet.
  */
  useEffect(() => {
    if (days && days.length > 0) {
      // calculate the from and to date of the selected month
      const [from, to] = DayjsUtils.calculateFromAndTo(days);

      if (times && times.length > 0) {
        // filter times by timestamp range
        const res = TimeUtils.filterByTimestampRange(from.unix(), to.unix(), times);
        setFilteredTimes(res);

        // filter absences by timestamp range
        const resAbsence = AbsenceUtils.getAbsencesByTimestampRange(absences, from.unix(), to.unix());
        setFilteredAbsences(resAbsence);

        // Release the download button, if data is available
        setDownloadBlocked(false);
      }
    }
  }, [days]);

  useEffect(() => {
    if (!profile) return;

    const results: ITimeCsv[] = [];
    days.forEach((day) => {
      const resultDay = day.format('YYYY-MM-DD');
      const resultWeek = TimeUtils.dateToWeek(day);

      //check if day is in future and skip it
      const isFuture = DayjsUtils.isFuture(day);
      if (isFuture) {
        return;
      }

      // check if day is tracked
      const times = filteredTimes?.filter((time) => time.day === day.format('YYYY-MM-DD'));
      if (times && times.length > 0) {
        times.forEach((time) => {
          const workingTimeBalance = time.workingTime - (profile?.workingtime || 0);
          const availableTimeBalance = time.availableTime - (profile?.availabletime || 0);
          results.push({
            week: resultWeek,
            weekDay: day.format('dddd'),
            day: resultDay,
            workingTime: time.workingTime,
            workingTimeBalance: workingTimeBalance,
            availableTime: time.availableTime,
            availableTimeBalance: availableTimeBalance,
            notes: time.notes,
          });
        });
        return;
      }

      // check if day is in planed absence
      const absences = filteredAbsences?.filter((absence) => absence.day === day.format('YYYY-MM-DD'));
      if (absences && absences.length > 0) {
        absences.forEach((absence) => {
          results.push({
            week: resultWeek,
            weekDay: day.format('dddd'),
            day: resultDay,
            workingTime: 0,
            workingTimeBalance: 0,
            availableTime: 0,
            availableTimeBalance: 0,
            notes: absence.absencetype,
          });
        });
        return;
      }

      // check if day is a holiday
      const isHoliday = HolidayUtils.isHoliday(day, profile.state);
      if (isHoliday) {
        results.push({
          week: resultWeek,
          weekDay: day.format('dddd'),
          day: resultDay,
          workingTime: 0,
          workingTimeBalance: 0,
          availableTime: 0,
          availableTimeBalance: 0,
          notes: HolidayUtils.whichHoliday(day, profile.state),
        });
        return;
      }

      // check if day is a workday
      const isWorkday = TimeUtils.checkWorkday(day, profile!.workingdays);
      if (!isWorkday) {
        results.push({
          week: resultWeek,
          weekDay: day.format('dddd'),
          day: resultDay,
          workingTime: 0,
          workingTimeBalance: 0,
          availableTime: 0,
          availableTimeBalance: 0,
          notes: 'Kein Arbeitstag',
        });
        return;
      }

      results.push({
        week: resultWeek,
        weekDay: day.format('dddd'),
        day: resultDay,
        workingTime: 0,
        workingTimeBalance: 0,
        availableTime: 0,
        availableTimeBalance: 0,
        notes: 'Fehlende Daten',
      });
    });

    setTimesExport(results);
  }, [filteredTimes]);

  const handleDownload = () => {
    if (!timesExport) {
      notifyContext.addNotification('Keine Daten zum Exportieren gefunden', 'error');
      return;
    }
    const firstDay = days[0];
    const month = days[0].month();
    const fileName = `report_${firstDay.format('MM-YYYY')}.pdf`;
    const exportMonth = firstDay.format('MMMM YYYY');
    const name = user?.displayName || '';

    // get the balance of the last month
    const previsousAvailableTimeBalance = evaluateTimes[month - 1]?.availableTime?.balance || 0;
    const previsousWorkingTimeBalance = evaluateTimes[month - 1]?.workingTime?.balance || 0;

    // get the balance of the current month
    const availableTimeBalanceCurrent = evaluateTimes[month]?.availableTime?.balance || 0;
    const workingTimeBalanceCurrent = evaluateTimes[month]?.workingTime?.balance || 0;

    exportPdf(
      timesExport,
      fileName,
      exportMonth,
      name,
      previsousWorkingTimeBalance,
      previsousAvailableTimeBalance,
      workingTimeBalanceCurrent,
      availableTimeBalanceCurrent
    );
  };

  return (
    <Stack direction="column" alignItems="center" spacing={2}>
      <TimesSvg width="150px" height="150px" />
      <MonthPicker onChange={handleDateRangePicker} />
      <Typography variant="body2">Exportiere deine Arbeitszeiten und Verfügungszeiten für den ausgewählten Monat.</Typography>

      {timesExport && (
        <Button fullWidth disabled={downloadBlocked} variant="contained" endIcon={<Download />} onClick={handleDownload}>
          Bericht herunterladen
        </Button>
      )}
    </Stack>
  );
};

export default ExportPage;
